// client/src/components/PhaseSwitcher.js
import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Menu,
  MenuItem,
  Typography,
  Tooltip,
  IconButton,
  Divider,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  School as SchoolIcon,
  ChildCare as ChildCareIcon,
  SwapHoriz as SwapIcon,
  Check as CheckIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { usePhase } from '../contexts/PhaseContext';

const PhaseSwitcher = ({ variant = 'button', size = 'medium' }) => {
  const {
    activePhase,
    availablePhases,
    switchPhase,
    getPhaseLabel,
    getPhaseDescription,
    canSwitchPhases,
    user,
    PHASES
  } = usePhase();
  
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  // Don't render if user can't switch phases
  if (!canSwitchPhases() || availablePhases.length <= 1) {
    return null;
  }

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handlePhaseSelect = (phase) => {
    switchPhase(phase);
    handleClose();
  };

  const getPhaseIcon = (phase) => {
    switch (phase) {
      case PHASES.NUR_PRI:
        return <ChildCareIcon />;
      case PHASES.SEC:
        return <SchoolIcon />;
      default:
        return <SchoolIcon />;
    }
  };

  const getPhaseColor = (phase) => {
    switch (phase) {
      case PHASES.NUR_PRI:
        return 'primary';
      case PHASES.SEC:
        return 'secondary';
      default:
        return 'default';
    }
  };

  if (variant === 'chip') {
    return (
      <Tooltip title={`Switch between ${availablePhases.map(p => getPhaseLabel(p)).join(' and ')}`}>
        <Chip
          icon={getPhaseIcon(activePhase)}
          label={getPhaseLabel(activePhase)}
          color={getPhaseColor(activePhase)}
          variant="outlined"
          clickable
          onClick={handleClick}
          size={size}
        />
      </Tooltip>
    );
  }

  if (variant === 'icon') {
    return (
      <Tooltip title={`Current: ${getPhaseLabel(activePhase)} - Click to switch`}>
        <IconButton
          onClick={handleClick}
          color={getPhaseColor(activePhase)}
          size={size}
        >
          {getPhaseIcon(activePhase)}
        </IconButton>
      </Tooltip>
    );
  }

  // Default button variant
  return (
    <Box>
      <Button
        variant="outlined"
        startIcon={getPhaseIcon(activePhase)}
        endIcon={<SwapIcon />}
        onClick={handleClick}
        color={getPhaseColor(activePhase)}
        size={size}
        sx={{
          minWidth: { xs: 0, sm: 200 },
          width: '100%',
          maxWidth: '100%',
          justifyContent: 'space-between',
          textTransform: 'none',
          fontWeight: 500,
          px: { xs: 1, sm: 1.5 },
          '& .MuiButton-startIcon': {
            mr: { xs: 0.75, sm: 1 }
          },
          '& .MuiButton-endIcon': {
            ml: { xs: 0.75, sm: 1 }
          }
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 0, flex: 1 }}>
          <Typography variant="body2" sx={{ lineHeight: 1.2, width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {getPhaseLabel(activePhase)}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1, width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {getPhaseDescription(activePhase)}
          </Typography>
        </Box>
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            minWidth: 280,
            maxHeight: 400
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Switch Phase
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Select the phase you want to work with
          </Typography>
        </Box>
        
        <Divider />
        
        {availablePhases.map((phase) => (
          <MenuItem
            key={phase.code}
            onClick={() => handlePhaseSelect(phase.code)}
            selected={phase.code === activePhase}
            sx={{
              py: 1.5,
              px: 2,
              '&.Mui-selected': {
                backgroundColor: 'action.selected',
                '&:hover': {
                  backgroundColor: 'action.hover'
                }
              }
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              {getPhaseIcon(phase)}
            </ListItemIcon>
            
            <ListItemText
              primary={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" fontWeight={phase.code === activePhase ? 600 : 400}>
                    {getPhaseLabel(phase.code)}
                  </Typography>
                  {phase.code === activePhase && (
                    <CheckIcon color="primary" sx={{ fontSize: 16 }} />
                  )}
                </Box>
              }
              secondary={
                <Typography variant="caption" color="text.secondary">
                  {getPhaseDescription(phase.code)}
                </Typography>
              }
            />
          </MenuItem>
        ))}
        
        <Divider />
        
        <Box sx={{ px: 2, py: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">
              All data will be filtered by the selected phase
            </Typography>
          </Box>
        </Box>
      </Menu>
    </Box>
  );
};

// Compact version for headers
export const PhaseSwitcherCompact = () => (
  <PhaseSwitcher variant="chip" size="small" />
);

// Icon version for toolbars
export const PhaseSwitcherIcon = () => (
  <PhaseSwitcher variant="icon" size="small" />
);

// Full version for main navigation
export const PhaseSwitcherFull = () => (
  <PhaseSwitcher variant="button" size="medium" />
);

export default PhaseSwitcher;